/**
 * SanitairLuxe — Product Page JavaScript
 * Handles: Gallery, Tabs, Sticky Cart, Quantity Selector, Variant Selection
 */

(function () {
  'use strict';

  /* ==========================================================================
     GALLERY — Thumbnail switching + Image Zoom
     ========================================================================== */

  class SLGallery {
    constructor(container) {
      this.container = container;
      if (!this.container) return;

      this.mainImage = this.container.querySelector('.sl-gallery__main-img');
      this.mainWrapper = this.container.querySelector('.sl-gallery__main');
      this.thumbs = this.container.querySelectorAll('.sl-gallery__thumb');
      this.zoomLens = this.container.querySelector('.sl-gallery__zoom-lens');
      this.videoItems = this.container.querySelectorAll('[data-media-type="video"]');

      this.bindThumbnails();
      this.bindZoom();
    }

    bindThumbnails() {
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          // Update active state
          this.thumbs.forEach((t) => t.classList.remove('is-active'));
          thumb.classList.add('is-active');

          const mediaSrc = thumb.dataset.mediaSrc;
          const mediaType = thumb.dataset.mediaType;
          const mediaHiRes = thumb.dataset.mediaHires;

          if (mediaType === 'video') {
            this.showVideo(mediaSrc);
          } else {
            this.showImage(mediaSrc, mediaHiRes);
          }
        });
      });
    }

    showImage(src, hiRes) {
      // Remove any video
      const video = this.mainWrapper.querySelector('video');
      if (video) video.remove();

      if (this.mainImage) {
        this.mainImage.style.display = 'block';
        this.mainImage.src = src;
        if (hiRes) {
          this.mainImage.dataset.hires = hiRes;
        }
      }

      // Hide video badge
      const badge = this.mainWrapper.querySelector('.sl-gallery__video-badge');
      if (badge) badge.style.display = 'none';
    }

    showVideo(src) {
      if (this.mainImage) this.mainImage.style.display = 'none';

      // Remove existing video
      const existingVideo = this.mainWrapper.querySelector('video');
      if (existingVideo) existingVideo.remove();

      const video = document.createElement('video');
      video.src = src;
      video.autoplay = true;
      video.loop = true;
      video.muted = true;
      video.playsInline = true;
      video.style.cssText = 'width:100%;height:100%;object-fit:cover;';
      this.mainWrapper.insertBefore(video, this.mainWrapper.firstChild);

      // Show video badge
      const badge = this.mainWrapper.querySelector('.sl-gallery__video-badge');
      if (badge) badge.style.display = 'flex';
    }

    bindZoom() {
      if (!this.mainWrapper || !this.zoomLens || !this.mainImage) return;

      this.mainWrapper.addEventListener('mouseenter', () => {
        if (window.innerWidth < 1024) return;
        this.mainWrapper.classList.add('is-zooming');
      });

      this.mainWrapper.addEventListener('mouseleave', () => {
        this.mainWrapper.classList.remove('is-zooming');
      });

      this.mainWrapper.addEventListener('mousemove', (e) => {
        if (window.innerWidth < 1024) return;
        if (!this.mainWrapper.classList.contains('is-zooming')) return;

        const rect = this.mainWrapper.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const lensW = this.zoomLens.offsetWidth;
        const lensH = this.zoomLens.offsetHeight;

        // Position lens
        this.zoomLens.style.left = `${x - lensW / 2}px`;
        this.zoomLens.style.top = `${y - lensH / 2}px`;

        // Zoom background
        const hiRes = this.mainImage.dataset.hires || this.mainImage.src;
        const zoomFactor = 2.5;
        const bgX = (x / rect.width) * 100;
        const bgY = (y / rect.height) * 100;

        this.zoomLens.style.backgroundImage = `url(${hiRes})`;
        this.zoomLens.style.backgroundSize = `${rect.width * zoomFactor}px ${rect.height * zoomFactor}px`;
        this.zoomLens.style.backgroundPosition = `${bgX}% ${bgY}%`;
      });
    }
  }

  /* ==========================================================================
     TABS — Tabbed content navigation
     ========================================================================== */

  class SLTabs {
    constructor(container) {
      this.container = container;
      if (!this.container) return;

      this.buttons = this.container.querySelectorAll('.sl-tabs__nav-btn');
      this.panels = this.container.querySelectorAll('.sl-tabs__panel');

      this.bind();
    }

    bind() {
      this.buttons.forEach((btn) => {
        btn.addEventListener('click', () => {
          const targetId = btn.dataset.tab;

          // Update buttons
          this.buttons.forEach((b) => b.classList.remove('is-active'));
          btn.classList.add('is-active');

          // Update panels
          this.panels.forEach((p) => {
            p.classList.remove('is-active');
            if (p.id === targetId) {
              p.classList.add('is-active');
            }
          });
        });
      });
    }
  }

  /* ==========================================================================
     QUANTITY SELECTOR
     ========================================================================== */

  class SLQuantity {
    constructor(container) {
      this.container = container;
      if (!this.container) return;

      this.input = this.container.querySelector('.sl-quantity__input');
      this.minusBtn = this.container.querySelector('[data-action="decrease"]');
      this.plusBtn = this.container.querySelector('[data-action="increase"]');

      if (!this.input) return;
      this.bind();
    }

    bind() {
      if (this.minusBtn) {
        this.minusBtn.addEventListener('click', () => {
          let val = parseInt(this.input.value) || 1;
          const min = parseInt(this.input.min) || 1;
          if (val > min) {
            this.input.value = val - 1;
            this.input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      if (this.plusBtn) {
        this.plusBtn.addEventListener('click', () => {
          let val = parseInt(this.input.value) || 1;
          const max = parseInt(this.input.max) || 99;
          if (val < max) {
            this.input.value = val + 1;
            this.input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      this.input.addEventListener('change', () => {
        let val = parseInt(this.input.value);
        const min = parseInt(this.input.min) || 1;
        if (isNaN(val) || val < min) this.input.value = min;
        const max = parseInt(this.input.max) || 99;
        if (val > max) this.input.value = max;
      });
    }
  }

  class SLKorverOrderRules {
    constructor() {
      this.page = document.querySelector('.sl-product-page[data-korver-minimum-quantity]');
      if (!this.page) return;

      this.minimumQuantity = Math.max(1, parseInt(this.page.dataset.korverMinimumQuantity) || 1);
      this.isKorver = this.page.dataset.korverSupplier === 'true';
      this.isAddOnOnly = this.page.dataset.korverAddOnOnly === 'true';
      this.minimumMessage = this.page.dataset.korverMinimumMessage || '';
      this.addOnMessage = this.page.dataset.korverAddOnMessage || '';
      this.forms = this.page.querySelectorAll('form[action*="/cart/add"], .sl-product-form');

      this.applyQuantityRules();
      this.forms.forEach((form) => this.applyLineItemProperties(form));
      this.showNotice();
      this.disableFastCheckout();
      this.installAddOnGuard();
    }

    applyQuantityRules() {
      this.page.querySelectorAll('input[name="quantity"], .sl-quantity__input').forEach((input) => {
        input.min = String(this.minimumQuantity);
        input.step = '1';
        if ((parseInt(input.value) || 0) < this.minimumQuantity) input.value = String(this.minimumQuantity);
      });
    }

    applyLineItemProperties(form) {
      if (!this.isKorver) return;
      this.addHiddenProperty(form, 'properties[_korver_supplier]', 'yes');
      this.addHiddenProperty(form, 'properties[_minimum_quantity]', String(this.minimumQuantity));
      this.addHiddenProperty(form, 'properties[_korver_add_on_only]', this.isAddOnOnly ? 'yes' : 'no');
    }

    addHiddenProperty(form, name, value) {
      let input = form.querySelector(`input[name="${name}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        form.appendChild(input);
      }
      input.value = value;
    }

    showNotice() {
      if (this.minimumQuantity <= 1 && !this.isAddOnOnly) return;
      const notice = document.createElement('p');
      notice.className = 'sl-korver-order-rule';
      notice.setAttribute('role', 'status');
      notice.textContent = this.isAddOnOnly ? this.addOnMessage : this.minimumMessage;
      const target = this.page.querySelector('.sl-quantity, form[action*="/cart/add"], .sl-product-form');
      if (target) target.insertAdjacentElement('beforebegin', notice);
    }

    disableFastCheckout() {
      if (this.minimumQuantity <= 1 && !this.isAddOnOnly) return;
      this.page.querySelectorAll('.shopify-payment-button, .sl-btn--buy-now').forEach((button) => {
        button.hidden = true;
      });
    }

    installAddOnGuard() {
      if (!this.isAddOnOnly) return;

      document.addEventListener('click', async (event) => {
        const button = event.target.closest('.sl-btn--add-to-cart, button[name="add"], form[action*="/cart/add"] button[type="submit"]');
        if (!button || !this.page.contains(button) || button.dataset.korverApproved === 'true') return;

        event.preventDefault();
        event.stopImmediatePropagation();
        if (button.dataset.korverChecking === 'true') return;
        button.dataset.korverChecking = 'true';

        const form = button.closest('form') || this.page.querySelector('form[action*="/cart/add"], .sl-product-form');
        const valid = form ? await this.validate(form) : false;
        delete button.dataset.korverChecking;
        if (!valid) return;

        button.dataset.korverApproved = 'true';
        button.click();
        queueMicrotask(() => delete button.dataset.korverApproved);
      }, true);
    }

    async validate(form) {
      const quantityInput = form.querySelector('input[name="quantity"], .sl-quantity__input');
      const quantity = parseInt(quantityInput?.value) || 1;
      if (quantity < this.minimumQuantity) {
        alert(this.minimumMessage);
        quantityInput?.focus();
        return false;
      }

      if (!this.isAddOnOnly) return true;

      try {
        const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        const cart = await response.json();
        const hasMainKorverProduct = cart.items.some((item) => {
          const properties = item.properties || {};
          return properties._korver_supplier === 'yes' && properties._korver_add_on_only !== 'yes';
        });
        if (hasMainKorverProduct) return true;
      } catch (error) {
        console.error('Korver cart validation failed:', error);
      }

      alert(this.addOnMessage);
      return false;
    }
  }

  /* ==========================================================================
     VARIANT SELECTION — Updates price, availability, and gallery
     ========================================================================== */

  class SLVariantSelector {
    constructor(container) {
      this.container = container;
      if (!this.container) return;

      this.productData = null;
      this.form = this.container.closest('form') || this.container.closest('[data-product-form]');

      try {
        const dataEl = this.container.querySelector('[data-product-json]');
        if (dataEl) {
          this.productData = JSON.parse(dataEl.textContent);
        }
      } catch (e) {
        console.warn('SLVariantSelector: Could not parse product JSON');
      }

      this.variantButtons = this.container.querySelectorAll('.sl-variant-btn');
      this.variantCards = this.container.querySelectorAll('.sl-variant-card');
      this.variantInput = this.container.querySelector('[name="id"]');
      this.priceEl = document.querySelector('.sl-price--current');
      this.comparePriceEl = document.querySelector('.sl-price--compare');
      this.addToCartBtn = document.querySelector('.sl-btn--add-to-cart');
      this.buyNowBtn = document.querySelector('.sl-btn--buy-now');
      this.stickyPrice = document.querySelector('.sl-sticky-cart__price');

      this.bindButtons();
      this.bindCards();
    }

    bindButtons() {
      this.variantButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
          if (btn.classList.contains('is-disabled')) return;

          // Deselect siblings in same group
          const group = btn.closest('.sl-variant-group');
          if (group) {
            group.querySelectorAll('.sl-variant-btn').forEach((b) => b.classList.remove('is-active'));
          }
          btn.classList.add('is-active');

          this.updateSelectedVariant();
        });
      });
    }

    bindCards() {
      this.variantCards.forEach((card) => {
        card.addEventListener('click', () => {
          if (card.classList.contains('is-unavailable')) return;

          this.variantCards.forEach((c) => c.classList.remove('is-active'));
          card.classList.add('is-active');

          const variantId = card.dataset.variantId;
          if (variantId && this.variantInput) {
            this.variantInput.value = variantId;
          }

          this.updatePriceFromCard(card);
        });
      });
    }

    updateSelectedVariant() {
      if (!this.productData || !this.productData.variants) return;

      // Collect selected options
      const selectedOptions = [];
      const groups = this.container.querySelectorAll('.sl-variant-group');
      groups.forEach((group) => {
        const active = group.querySelector('.sl-variant-btn.is-active');
        if (active) {
          selectedOptions.push(active.dataset.value);
        }
      });

      // Find matching variant
      const variant = this.productData.variants.find((v) => {
        return v.options.every((opt, i) => opt === selectedOptions[i]);
      });

      if (variant) {
        this.updateVariant(variant);
      }
    }

    updateVariant(variant) {
      // Update hidden input
      if (this.variantInput) {
        this.variantInput.value = variant.id;
      }

      // Update price
      if (this.priceEl) {
        this.priceEl.textContent = this.formatMoney(variant.price);
      }
      if (this.comparePriceEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          this.comparePriceEl.textContent = this.formatMoney(variant.compare_at_price);
          this.comparePriceEl.style.display = 'inline';
        } else {
          this.comparePriceEl.style.display = 'none';
        }
      }

      // Update sticky cart price
      if (this.stickyPrice) {
        this.stickyPrice.textContent = this.formatMoney(variant.price);
      }

      // Update button availability
      if (this.addToCartBtn) {
        if (variant.available) {
          this.addToCartBtn.disabled = false;
          this.addToCartBtn.querySelector('.sl-btn__text').textContent =
            theme?.variantStrings?.addToCart || 'In winkelwagen';
        } else {
          this.addToCartBtn.disabled = true;
          this.addToCartBtn.querySelector('.sl-btn__text').textContent =
            theme?.variantStrings?.soldOut || 'Uitverkocht';
        }
      }

      // Update URL
      if (history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url.toString());
      }

      // Dispatch event for other components
      document.dispatchEvent(
        new CustomEvent('sl:variant:change', { detail: { variant } })
      );
    }

    updatePriceFromCard(card) {
      const price = card.dataset.price;
      const comparePrice = card.dataset.comparePrice;

      if (price && this.priceEl) {
        this.priceEl.textContent = price;
      }
      if (this.stickyPrice && price) {
        this.stickyPrice.textContent = price;
      }
      if (this.comparePriceEl) {
        if (comparePrice) {
          this.comparePriceEl.textContent = comparePrice;
          this.comparePriceEl.style.display = 'inline';
        } else {
          this.comparePriceEl.style.display = 'none';
        }
      }
    }

    formatMoney(cents) {
      if (typeof cents === 'string') cents = parseInt(cents);
      const format = theme?.settings?.money_with_currency_format || '€{{amount}}';
      const amount = (cents / 100).toFixed(2).replace('.', ',');
      return format.replace('{{amount}}', amount).replace('{{amount_with_comma_separator}}', amount);
    }
  }

  /* ==========================================================================
     STICKY ADD TO CART — Shows on scroll past main ATC button
     ========================================================================== */

  class SLStickyCart {
    constructor() {
      this.stickyBar = document.querySelector('.sl-sticky-cart');
      this.addToCartBtn = document.querySelector('.sl-btn--add-to-cart');

      if (!this.stickyBar) return;
      this.bind();
    }

    bind() {
      let ticking = false;

      const checkVisibility = () => {
        if (!this.addToCartBtn) {
          this.stickyBar.classList.add('is-visible');
          return;
        }

        const rect = this.addToCartBtn.getBoundingClientRect();
        const isOutOfView = rect.bottom < 0;

        if (isOutOfView) {
          this.stickyBar.classList.add('is-visible');
        } else {
          this.stickyBar.classList.remove('is-visible');
        }
        ticking = false;
      };

      window.addEventListener('scroll', () => {
        if (!ticking) {
          requestAnimationFrame(checkVisibility);
          ticking = true;
        }
      }, { passive: true });

      // Sticky cart button click → scroll to main ATC or submit form
      const stickyBtn = this.stickyBar.querySelector('.sl-sticky-cart__btn');
      if (stickyBtn) {
        stickyBtn.addEventListener('click', (e) => {
          e.preventDefault();
          const form = document.querySelector('.sl-product-form');
          if (form) {
            // Submit the main product form
            const addBtn = form.querySelector('.sl-btn--add-to-cart');
            if (addBtn) addBtn.click();
          }
        });
      }
    }
  }

  /* ==========================================================================
     ADD TO CART — AJAX form submission
     ========================================================================== */

  class SLAddToCart {
    constructor() {
      this.forms = document.querySelectorAll('.sl-product-form');
      this.forms.forEach((form) => this.bindForm(form));
    }

    bindForm(form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();

        if (window.SLKorverOrderRules) {
          const valid = await window.SLKorverOrderRules.validate(form);
          if (!valid) return;
        }

        const btn = form.querySelector('.sl-btn--add-to-cart');
        if (!btn || btn.disabled) return;

        btn.classList.add('sl-btn--loading');

        const formData = new FormData(form);

        try {
          const response = await fetch(theme?.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            body: formData,
          });

          const data = await response.json();

          if (response.ok) {
            // Success — refresh cart drawer HTML in background
            document.dispatchEvent(new CustomEvent('cart:refresh'));

            // Show add-to-cart success modal (sl:cart:added listener in sl-cart-modal.js)
            document.dispatchEvent(new CustomEvent('sl:cart:added', { detail: data }));
          } else {
            alert(data.description || 'Er is een fout opgetreden.');
          }
        } catch (err) {
          console.error('Add to cart failed:', err);
          alert('Er is een fout opgetreden bij het toevoegen aan de winkelwagen.');
        } finally {
          btn.classList.remove('sl-btn--loading');
        }
      });
    }
  }

  /* ==========================================================================
     INIT — Initialize all components on DOM ready
     ========================================================================== */

  function init() {
    // Gallery
    document.querySelectorAll('.sl-gallery').forEach((el) => new SLGallery(el));

    // Tabs
    document.querySelectorAll('.sl-tabs-section').forEach((el) => new SLTabs(el));

    // Quantity selectors
    document.querySelectorAll('.sl-quantity').forEach((el) => new SLQuantity(el));

    // Variant selectors
    document.querySelectorAll('[data-sl-variant-selector]').forEach((el) => new SLVariantSelector(el));

    // Sticky cart
    new SLStickyCart();

    // Add to cart
    window.SLKorverOrderRules = new SLKorverOrderRules();
    new SLAddToCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize on Shopify theme editor section events
  if (Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', init);
  }
})();
